// Pure ordering/display helpers for Binder lists (the list-reorder spec §3, §5, §6).
//
// Order is a per-entry fractional `position` reconciled LWW on `positionedAt`
// (see `listMerge.ts`). These helpers are platform-agnostic so web and the native
// ports share identical semantics; the drag UIs only translate a drop into a pair
// of neighbor ids and persist the result like any other list edit.

import type { ListEntry, VaultItem } from './index';
import { keyBetween } from './fractionalIndex';

/** Live (non-tombstoned) entries. */
function live(item: VaultItem): ListEntry[] {
  return (item.listItems ?? []).filter((e) => !e.deleted);
}

/** Total display order. Positioned entries sort by `position` (tie-break `id`);
 *  a fully-legacy list (no positions) falls back to `addedAt` order — today's
 *  behavior. The mixed state is transient (see `ensurePositions`); positioned
 *  entries sort ahead of unpositioned ones in it. */
export function compareEntries(a: ListEntry, b: ListEntry): number {
  const ap = a.position;
  const bp = b.position;
  if (ap !== undefined && bp !== undefined) {
    if (ap !== bp) return ap < bp ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  }
  if (ap !== undefined) return -1; // positioned before unpositioned
  if (bp !== undefined) return 1;
  if (a.addedAt !== b.addedAt) return a.addedAt < b.addedAt ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Live entries in display order. `includeDone === false` drops checked items
 * (the local, per-surface "show done" toggle — never a synced change). Checked
 * items otherwise keep their manual slot (no done-sink).
 */
export function displayEntries(item: VaultItem, includeDone = true): ListEntry[] {
  const entries = live(item).filter((e) => includeDone || !e.done);
  return entries.sort(compareEntries);
}

/**
 * Assign a `position` to every live entry that lacks one, in `(addedAt, id)`
 * order, deterministically (`positionedAt = addedAt`), so two clients migrating
 * the same legacy list concurrently compute identical values and the position
 * LWW is a no-op tie. No-op (returns the same item) once every live entry is
 * positioned. Tombstoned entries are left untouched.
 */
export function ensurePositions(item: VaultItem): VaultItem {
  const liveEntries = live(item);
  if (liveEntries.length === 0 || liveEntries.every((e) => e.position !== undefined)) {
    return item;
  }
  const order = [...liveEntries].sort((a, b) =>
    a.addedAt !== b.addedAt ? (a.addedAt < b.addedAt ? -1 : 1) : a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
  );
  const assigned = new Map<string, string>();
  let prev: string | null = null;
  for (const e of order) {
    const pos: string = e.position ?? keyBetween(prev, null);
    assigned.set(e.id, pos);
    prev = pos;
  }
  const listItems = (item.listItems ?? []).map((e) => {
    if (e.deleted || e.position !== undefined) return e;
    const pos = assigned.get(e.id);
    return pos === undefined ? e : { ...e, position: pos, positionedAt: e.addedAt };
  });
  return { ...item, listItems };
}

/**
 * Move `entryId` to sit between the visible neighbors `prevId` and `nextId`
 * (either may be `null` for an end of the visible list). The caller passes the
 * neighbors around the drop slot in whatever list the user currently sees (so
 * hiding done items is handled naturally). Stamps attribution + `positionedAt`
 * and bumps `updatedAt`; persists/pushes like any other list edit.
 */
export function moveEntry(
  item: VaultItem,
  entryId: string,
  prevId: string | null,
  nextId: string | null,
  by: string,
  now: string = new Date().toISOString(),
): VaultItem {
  const ensured = ensurePositions(item);
  const entries = ensured.listItems ?? [];
  const posOf = (id: string | null): string | null =>
    id === null ? null : entries.find((e) => e.id === id)?.position ?? null;
  const prevPos = posOf(prevId);
  const nextPos = posOf(nextId);
  // Guard against a degenerate/stale neighbor pair (prev >= next): no-op.
  if (prevPos !== null && nextPos !== null && prevPos >= nextPos) return ensured;
  const position = keyBetween(prevPos, nextPos);
  const listItems = entries.map((e) =>
    e.id === entryId && !e.deleted
      ? { ...e, position, positionedAt: now, positionedBy: by }
      : e,
  );
  return { ...ensured, listItems, updatedAt: now };
}
